import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { MachineConfigApi } from '@/api';
import { Plus, Edit, Trash2 } from 'lucide-react';

const FAMILY_LABELS: Record<string, string> = {
  nova: 'Nova', nano: 'Nano', orbiton: 'Orbiton', roll_format: 'Roll Format',
};

const PRINTHEAD_TYPE_OPTIONS = ['Ricoh Gen 6', 'Epson S3200'];

const EMPTY_FORM = {
  family: 'nova' as 'nova' | 'nano' | 'orbiton' | 'roll_format',
  model_key: '',
  name: '',
  description: '',
  colour_count: '',
  colour_config: '',
  pallet_count: '',
  head_options: '',
  print_area: '',
  single_step: '',
  standard_accessories: '',
  screen_station: '',
  pallet_size: '',
  printhead_types: [] as string[],
  hsn_sac: '',
  gst_rate: '18',
  uom: 'Nos.',
  lead_time_weeks: '0',
};

export default function MachineConfigs() {
  const { push } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [family, setFamily] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const configsQuery = useQuery({
    queryKey: ['machineConfigsAdmin', family],
    queryFn: () => MachineConfigApi.list({ ...(family && { family }) }),
  });
  const configs = configsQuery.data?.data ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['machineConfigsAdmin'] });
    queryClient.invalidateQueries({ queryKey: ['machineConfigs'] });
  };

  const createMutation = useMutation({
    mutationFn: (data: any) => MachineConfigApi.create(data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Machine configuration created' });
      setModalOpen(false);
      resetForm();
      invalidate();
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to create configuration', body: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => MachineConfigApi.update(editId!, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Machine configuration updated' });
      setModalOpen(false);
      resetForm();
      invalidate();
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update configuration', body: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => MachineConfigApi.deactivate(id),
    onSuccess: () => {
      push({ tone: 'success', title: 'Machine configuration removed' });
      invalidate();
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to remove configuration', body: err.message });
    },
  });

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.model_key.trim()) newErrors.model_key = 'Model key is required (e.g. nova-32)';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.uom.trim()) newErrors.uom = 'UOM is required';
    if (formData.head_options.trim()) {
      const parts = formData.head_options.split(',').map((s) => s.trim()).filter(Boolean);
      const invalid = parts.some((p) => !/^\d+$/.test(p) || Number(p) <= 0);
      if (invalid) newErrors.head_options = 'Comma-separated positive whole numbers only, e.g. 2, 4, 6, 8';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEdit = (cfg: any) => {
    setFormData({
      family: cfg.family,
      model_key: cfg.model_key,
      name: cfg.name,
      description: cfg.description ?? '',
      colour_count: cfg.colour_count ?? '',
      colour_config: cfg.colour_config ?? '',
      pallet_count: cfg.pallet_count ?? '',
      head_options: Array.isArray(cfg.head_options) ? cfg.head_options.join(', ') : '',
      print_area: cfg.print_area ?? '',
      single_step: cfg.single_step ?? '',
      standard_accessories: cfg.standard_accessories ?? '',
      screen_station: cfg.screen_station ?? '',
      pallet_size: cfg.pallet_size ?? '',
      printhead_types: Array.isArray(cfg.printhead_types) ? cfg.printhead_types : [],
      hsn_sac: cfg.hsn_sac ?? '',
      gst_rate: cfg.gst_rate ?? '18',
      uom: cfg.uom ?? 'Nos.',
      lead_time_weeks: cfg.lead_time_weeks ?? '0',
    });
    setEditId(cfg.id);
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const data = {
      family: formData.family,
      model_key: formData.model_key.trim(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      colour_count: formData.colour_count ? Number(formData.colour_count) : null,
      colour_config: formData.colour_config.trim() || null,
      pallet_count: formData.pallet_count ? Number(formData.pallet_count) : null,
      head_options: formData.head_options.trim()
        ? formData.head_options.split(',').map((s) => Number(s.trim())).filter(Boolean)
        : null,
      print_area: formData.print_area.trim() || null,
      single_step: formData.single_step.trim() || null,
      standard_accessories: formData.standard_accessories.trim() || null,
      screen_station: formData.screen_station ? Number(formData.screen_station) : null,
      pallet_size: formData.pallet_size.trim() || null,
      printhead_types: formData.printhead_types.length ? formData.printhead_types : null,
      hsn_sac: formData.hsn_sac.trim() || null,
      gst_rate: Number(formData.gst_rate),
      uom: formData.uom.trim(),
      lead_time_weeks: Number(formData.lead_time_weeks),
    };
    if (editId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Machine Configurations"
        subtitle="Add and edit every Nova, Nano, Orbiton and Roll Format configuration. Price bands are set separately in Price Config (admin only) — a PI cannot be created with a config that has no price band set."
        actions={
          <button className="btn btn-primary gap-2" onClick={handleOpenCreate}>
            <Plus size={16} /> New machine config
          </button>
        }
      />

      <div className="card card-pad flex flex-wrap gap-4 items-end">
        <div>
          <label className="label">Family</label>
          <select className="input mt-1" value={family} onChange={(e) => setFamily(e.target.value)}>
            <option value="">All</option>
            <option value="nova">Nova</option>
            <option value="nano">Nano</option>
            <option value="orbiton">Orbiton</option>
            <option value="roll_format">Roll Format</option>
          </select>
        </div>
      </div>

      {configsQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : configs.length === 0 ? (
        <EmptyState
          title="No machine configurations found"
          body="Add your first machine configuration to get started."
          action={
            <button className="btn btn-primary" onClick={handleOpenCreate}>
              New machine config
            </button>
          }
        />
      ) : (
        <div className="card flex flex-col md:max-h-[calc(100vh-260px)]">
          <div className="overflow-auto flex-1">
            <table className="table-clean">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Family</th>
                  <th>Name</th>
                  <th>Spec</th>
                  <th>Price Range (INR)</th>
                  <th>Price Range (USD)</th>
                  <th>Price Range (EUR)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c: any) => (
                  <tr key={c.id}>
                    <td className="text-sm text-muted">{FAMILY_LABELS[c.family]}</td>
                    <td className="font-medium">{c.name}</td>
                    <td className="text-xs text-muted max-w-xs">
                      <ul className="list-disc list-inside space-y-0.5">
                        {[
                          c.colour_config,
                          c.head_options?.length ? `${c.head_options.join('/')} heads` : null,
                          c.pallet_count != null ? `${c.pallet_count} pallets` : null,
                          c.printhead_types?.length ? c.printhead_types.join(' / ') : null,
                          c.screen_station != null ? `${c.screen_station} screen stations` : null,
                          c.pallet_size ? `Pallet ${c.pallet_size}` : null,
                          c.print_area ? `Print Area ${c.print_area}` : null,
                          c.single_step ? `Step ${c.single_step}` : null,
                          c.standard_accessories,
                        ].filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
                      </ul>
                      {(c.family === 'nova' || c.family === 'nano') && (!c.head_options || c.head_options.length === 0) && (
                        <span className="text-amber-600">head-count list not set — cannot be added to a PI yet</span>
                      )}
                    </td>
                    <td className="text-sm">
                      {c.min_price_inr && c.max_price_inr
                        ? `₹${Number(c.min_price_inr).toLocaleString('en-IN')} - ₹${Number(c.max_price_inr).toLocaleString('en-IN')}`
                        : <span className="text-rdx">Not set</span>}
                    </td>
                    <td className="text-sm">
                      {c.min_price_usd && c.max_price_usd
                        ? `$${Number(c.min_price_usd).toLocaleString('en-US')} - $${Number(c.max_price_usd).toLocaleString('en-US')}`
                        : <span className="text-muted">-</span>}
                    </td>
                    <td className="text-sm">
                      {c.min_price_eur && c.max_price_eur
                        ? `€${Number(c.min_price_eur).toLocaleString('en-US')} - €${Number(c.max_price_eur).toLocaleString('en-US')}`
                        : <span className="text-muted">-</span>}
                    </td>
                    <td className="align-middle">
                      <div className="flex gap-1 items-center">
                        <button className="btn btn-sm btn-ghost" onClick={() => handleOpenEdit(c)}>
                          <Edit size={14} />
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteMutation.mutate(c.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title={editId ? 'Edit Machine Configuration' : 'New Machine Configuration'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setModalOpen(false); resetForm(); }}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-3 max-h-96 overflow-y-auto">
          <div>
            <label className="label">Family *</label>
            <select
              className="input mt-1"
              value={formData.family}
              onChange={(e) => setFormData({ ...formData, family: e.target.value as any })}
            >
              <option value="nova">Nova</option>
              <option value="nano">Nano</option>
              <option value="orbiton">Orbiton</option>
              <option value="roll_format">Roll Format</option>
            </select>
          </div>

          <div>
            <label className="label">Model key * <span className="text-muted font-normal">(internal, unique, e.g. nova-32)</span></label>
            <input
              type="text"
              className={`input mt-1 ${errors.model_key ? 'input-error' : ''}`}
              value={formData.model_key}
              onChange={(e) => setFormData({ ...formData, model_key: e.target.value })}
              disabled={!!editId}
            />
            {errors.model_key && <div className="text-xs text-rdx mt-1">{errors.model_key}</div>}
          </div>

          <div>
            <label className="label">Display name *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Nova 32 (8 Colour CMYKRGOB)"
            />
            {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              className="input mt-1"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          {/* Spec fields shown here are scoped to the selected family — each
              machine type only exposes the specs that actually apply to it. */}
          {(formData.family === 'nova' || formData.family === 'nano' || formData.family === 'roll_format') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Colour count</label>
                <input
                  type="number"
                  className="input mt-1"
                  value={formData.colour_count}
                  onChange={(e) => setFormData({ ...formData, colour_count: e.target.value })}
                  min={1}
                />
              </div>
              <div>
                <label className="label">Colour config</label>
                <input
                  type="text"
                  className="input mt-1"
                  value={formData.colour_config}
                  onChange={(e) => setFormData({ ...formData, colour_config: e.target.value })}
                  placeholder="e.g. CMYK, CMYKRG"
                />
              </div>
            </div>
          )}

          {(formData.family === 'nova' || formData.family === 'nano') && (
            <div>
              <label className="label">Print Head(s)</label>
              <input
                type="text"
                className={`input mt-1 ${errors.head_options ? 'input-error' : ''}`}
                value={formData.head_options}
                onChange={(e) => setFormData({ ...formData, head_options: e.target.value })}
                placeholder="e.g. 2, 4, 6, 8, 10, 12, 14, 16"
              />
              {errors.head_options && <div className="text-xs text-rdx mt-1">{errors.head_options}</div>}
            </div>
          )}

          {formData.family === 'orbiton' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Colour count <span className="text-muted font-normal">(caps Flash Heater)</span></label>
                <input
                  type="number"
                  className="input mt-1"
                  value={formData.colour_count}
                  onChange={(e) => setFormData({ ...formData, colour_count: e.target.value })}
                  min={1}
                />
              </div>
              <div>
                <label className="label">Pallets</label>
                <input
                  type="number"
                  className="input mt-1"
                  value={formData.pallet_count}
                  onChange={(e) => setFormData({ ...formData, pallet_count: e.target.value })}
                  min={1}
                />
              </div>
            </div>
          )}

          {formData.family === 'orbiton' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Screen Station <span className="text-muted font-normal">(optional)</span></label>
                <input
                  type="number"
                  className="input mt-1"
                  value={formData.screen_station}
                  onChange={(e) => setFormData({ ...formData, screen_station: e.target.value })}
                  min={1}
                />
              </div>
              <div>
                <label className="label">Pallet Size <span className="text-muted font-normal">(optional)</span></label>
                <input
                  type="text"
                  className="input mt-1"
                  value={formData.pallet_size}
                  onChange={(e) => setFormData({ ...formData, pallet_size: e.target.value })}
                  placeholder="e.g. 85cm x 65cm"
                />
              </div>
            </div>
          )}

          {formData.family === 'roll_format' && (
            <div>
              <label className="label">
                Printhead Types <span className="text-muted font-normal">(select all this machine supports; the PI creator picks one)</span>
              </label>
              <div className="flex gap-4 mt-1">
                {PRINTHEAD_TYPE_OPTIONS.map((t) => (
                  <label key={t} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.printhead_types.includes(t)}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          printhead_types: e.target.checked
                            ? [...formData.printhead_types, t]
                            : formData.printhead_types.filter((x) => x !== t),
                        })
                      }
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Print Area <span className="text-muted font-normal">(optional)</span></label>
              <input
                type="text"
                className="input mt-1"
                value={formData.print_area}
                onChange={(e) => setFormData({ ...formData, print_area: e.target.value })}
                placeholder="e.g. 55cm x 75cm"
              />
            </div>
            {(formData.family === 'nova' || formData.family === 'nano') && (
              <div>
                <label className="label">Single Step <span className="text-muted font-normal">(optional)</span></label>
                <input
                  type="text"
                  className="input mt-1"
                  value={formData.single_step}
                  onChange={(e) => setFormData({ ...formData, single_step: e.target.value })}
                  placeholder="e.g. 30cm"
                />
              </div>
            )}
          </div>

          <div>
            <label className="label">Standard Accessories <span className="text-muted font-normal">(optional)</span></label>
            <textarea
              className="input mt-1"
              value={formData.standard_accessories}
              onChange={(e) => setFormData({ ...formData, standard_accessories: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">HSN/SAC Code</label>
              <input
                type="text"
                className="input mt-1"
                value={formData.hsn_sac}
                onChange={(e) => setFormData({ ...formData, hsn_sac: e.target.value })}
              />
            </div>
            <div>
              <label className="label">GST Rate (%) *</label>
              <input
                type="number"
                className="input mt-1"
                value={formData.gst_rate}
                onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                step={0.01}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">UOM *</label>
              <input
                type="text"
                className={`input mt-1 ${errors.uom ? 'input-error' : ''}`}
                value={formData.uom}
                onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
              />
              {errors.uom && <div className="text-xs text-rdx mt-1">{errors.uom}</div>}
            </div>
            <div>
              <label className="label">Lead Time (Weeks)</label>
              <input
                type="number"
                className="input mt-1"
                value={formData.lead_time_weeks}
                onChange={(e) => setFormData({ ...formData, lead_time_weeks: e.target.value })}
                min={0}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
